import { auth } from "./auth.service";
import { practiceDataService } from "./practice-data.service";

/**
 * Lern-Streak: an wie vielen Tagen in Folge wurde geübt.
 *
 * Die Tage liegen lokal (localStorage) – der Streak muss beim Öffnen einer
 * Übung SOFORT dastehen, ein Serveraufruf dafür wäre nur Wartezeit. Die
 * Wahrheit für andere Geräte bleibt `practice_data.learnStats`; von dort
 * werden die Tage einmal je Sitzung im Hintergrund nachgezogen und mit den
 * lokalen vereinigt.
 */

const KEY = "lernapp.streakdays.v1";
const DAY = 86_400_000;

const iso = (d: Date) => d.toISOString().slice(0, 10);
const todayIso = () => iso(new Date());

function readDays(): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "[]") as string[];
    return new Set(Array.isArray(raw) ? raw : []);
  } catch {
    return new Set();
  }
}

function writeDays(days: Set<string>): void {
  // Nur das letzte Jahr behalten – alles davor beeinflusst keinen Streak mehr.
  const limit = iso(new Date(Date.now() - 366 * DAY));
  const kept = [...days].filter((d) => d >= limit).sort();
  try {
    localStorage.setItem(KEY, JSON.stringify(kept));
  } catch {
    /* ignore */
  }
}

/** Heute als Lerntag markieren. Gibt den aktuellen Streak zurück. */
export function markPracticed(): number {
  const days = readDays();
  days.add(todayIso());
  writeDays(days);
  return streakOf(days);
}

/** Aktueller Streak. Ein noch nicht geübter heutiger Tag bricht ihn nicht. */
export function currentStreak(): number {
  return streakOf(readDays());
}

function streakOf(days: Set<string>): number {
  let n = 0;
  const cursor = new Date();
  // Wer heute noch nicht geübt hat, steht trotzdem auf dem Streak von gestern –
  // sonst zeigte die App den ganzen Vormittag über eine 0 an und entmutigt.
  if (!days.has(iso(cursor))) cursor.setTime(cursor.getTime() - DAY);
  for (;;) {
    if (!days.has(iso(cursor))) break;
    n++;
    cursor.setTime(cursor.getTime() - DAY);
  }
  return n;
}

/** Hat der Nutzer heute schon geübt? (Für „Tagesziel geschafft".) */
export function practicedToday(): boolean {
  return readDays().has(todayIso());
}

/** Die letzten `n` Tage als Flags – für die Wochenleiste im Pfad. */
export function lastDays(n = 7): { date: string; done: boolean }[] {
  const days = readDays();
  const out: { date: string; done: boolean }[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = iso(new Date(Date.now() - i * DAY));
    out.push({ date: d, done: days.has(d) });
  }
  return out;
}

let synced = false;

/**
 * Einmal je Sitzung die Lerntage vom Server dazuholen. Läuft im Hintergrund
 * und darf ruhig scheitern – der lokale Streak steht dann eben nur für dieses
 * Gerät.
 */
export async function syncStreak(): Promise<void> {
  if (synced || !auth.isAuthenticated()) return;
  synced = true;
  try {
    const perTopic = await practiceDataService.listAllLearnStats();
    const days = readDays();
    for (const stats of perTopic) {
      for (const s of stats) {
        const date = (s as { date?: string }).date;
        if (date) days.add(String(date).slice(0, 10));
      }
    }
    writeDays(days);
  } catch {
    /* offline oder Collection fehlt – lokaler Streak bleibt gültig */
  }
}
